import { useMemo } from 'react';
import { useCentralizedProviderData } from '@crehana/auth';
import { useDisallowedRoute } from "./use-disallowed-route";
import { useHasPermissions } from "./use-has-permissions";
import { usePathname } from "./use-pathname";

// Organizations not allowed to access Hana AI (temporary hotfix)
// In case of doubts, contact Alejandra Hernandez and Alejandra Guevara
var disallowedOrganizations = ['universidad-comex'];

// ===== PATH UTILITIES =====
var getPathSegments = function getPathSegments(pathname) {
  var cleanPath = pathname.split('?')[0].split('#')[0];
  return cleanPath.split('/').filter(Boolean);
};
var isAdminPath = function isAdminPath(segments) {
  return segments.some(function (s) {
    return s === 'admin' || s === 'panel';
  });
};

// ===== USER PERMISSIONS HOOK =====
export var useUserPermissions = function useUserPermissions(_ref) {
  var _me$security2, _me$security2$learnin, _me$security4, _me$security4$central, _me$security5, _me$security5$learnin, _me$security6, _me$security7, _me$security7$learnin;
  var currentViewMode = _ref.currentViewMode;
  var _useCentralizedProvid = useCentralizedProviderData(),
    me = _useCentralizedProvid.me,
    refetch = _useCentralizedProvid.refetch,
    loading = _useCentralizedProvid.loading;
  var pathname = usePathname();
  var _useDisallowedRoute = useDisallowedRoute(),
    isRouteDisallowed = _useDisallowedRoute.isRouteDisallowed;

  // Path detection
  var segments = getPathSegments(pathname);
  var isAdminPathResult = isAdminPath(segments);

  // User data extraction
  var learningOrganizationId = useMemo(function () {
    var _me$security, _me$security$learning;
    var orgId = me === null || me === void 0 ? void 0 : (_me$security = me.security) === null || _me$security === void 0 ? void 0 : (_me$security$learning = _me$security.learningSession) === null || _me$security$learning === void 0 ? void 0 : _me$security$learning.organizationId;
    return orgId != null ? Number(orgId) : undefined;
  }, [me === null || me === void 0 ? void 0 : (_me$security2 = me.security) === null || _me$security2 === void 0 ? void 0 : (_me$security2$learnin = _me$security2.learningSession) === null || _me$security2$learnin === void 0 ? void 0 : _me$security2$learnin.organizationId]);
  var centralizedOrganizationId = useMemo(function () {
    var _me$security3, _me$security3$central;
    var orgId = me === null || me === void 0 ? void 0 : (_me$security3 = me.security) === null || _me$security3 === void 0 ? void 0 : (_me$security3$central = _me$security3.centralizedSession) === null || _me$security3$central === void 0 ? void 0 : _me$security3$central.centralizedOrganizationId;
    return orgId != null ? Number(orgId) : undefined;
  }, [me === null || me === void 0 ? void 0 : (_me$security4 = me.security) === null || _me$security4 === void 0 ? void 0 : (_me$security4$central = _me$security4.centralizedSession) === null || _me$security4$central === void 0 ? void 0 : _me$security4$central.centralizedOrganizationId]);
  var slug = me === null || me === void 0 ? void 0 : (_me$security5 = me.security) === null || _me$security5 === void 0 ? void 0 : (_me$security5$learnin = _me$security5.learningSession) === null || _me$security5$learnin === void 0 ? void 0 : _me$security5$learnin.organizationSlug;
  var applicationPermissions = (me === null || me === void 0 ? void 0 : (_me$security6 = me.security) === null || _me$security6 === void 0 ? void 0 : _me$security6.applicationPermissions) || [];

  // User validation
  var isUserValid = Boolean(me === null || me === void 0 ? void 0 : (_me$security7 = me.security) === null || _me$security7 === void 0 ? void 0 : (_me$security7$learnin = _me$security7.learningSession) === null || _me$security7$learnin === void 0 ? void 0 : _me$security7$learnin.organizationId);
  var isUserSessionValid = isUserValid;

  // Permission checks
  var _useHasPermissions = useHasPermissions(),
    hasHana = _useHasPermissions.hasHana,
    hasHanaAdminPermission = _useHasPermissions.hasHanaAdminPermission,
    hasUserPermission = _useHasPermissions.hasUserPermission;
  var canAccessHanaAI = useMemo(function () {
    // Check if the organization is in the disallowed list
    if (slug && disallowedOrganizations.includes(slug)) {
      return false;
    }

    // Check if the current route is explicitly disallowed for Hana AI
    if (isRouteDisallowed) {
      return false;
    }

    // Require Hana AI product access AND (admin or user permissions)
    return isUserValid && hasHana && (hasHanaAdminPermission || hasUserPermission);
  }, [isUserValid, hasHana, slug, isRouteDisallowed, hasHanaAdminPermission, hasUserPermission]);
  var viewModeWithPermissions = useMemo(function () {
    if (currentViewMode) {
      return currentViewMode;
    }

    // If it's an admin path, check if user has admin permissions AND route is not disallowed
    if (isAdminPathResult && hasHanaAdminPermission && !isRouteDisallowed) {
      return 'admin';
    }

    // If it's an admin path but user doesn't have admin permissions,
    // or if the route is disallowed, fall back to end-user view
    if (isAdminPathResult && (!hasHanaAdminPermission || isRouteDisallowed)) {
      return 'end-user';
    }

    // For non-admin paths, use end-user view
    return 'end-user';
  }, [isAdminPathResult, hasHanaAdminPermission, isRouteDisallowed, currentViewMode]);
  return {
    // Path & View Mode
    viewMode: viewModeWithPermissions,
    isAdminPath: isAdminPathResult,
    pathname: pathname,
    // User Data
    me: me,
    learningOrganizationId: learningOrganizationId,
    centralizedOrganizationId: centralizedOrganizationId,
    slug: slug,
    refetch: refetch,
    loading: loading,
    // User Validation
    isUserValid: isUserValid,
    isUserSessionValid: isUserSessionValid,
    applicationPermissions: applicationPermissions,
    // Access Control
    canAccessHanaAI: canAccessHanaAI,
    // Permission details for debugging/logging
    hasHanaAdminPermission: hasHanaAdminPermission,
    hasUserPermission: hasUserPermission,
    // Route permission details for debugging/logging
    isRouteDisallowedForHanaAI: isRouteDisallowed
  };
};